
import { Signal } from "signals";
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import { AnticipationInfo } from "slotworks/model/gameplay/records/spin-record";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import { parallel, sequence } from "slotworks/utils/race-utils";
import gameState from "slotworks/model/game-state";
import G288SpinRecord from "integration/spin-record";
import G288Components from "components/components";
import G288ReelSpinner from "./reel-spinner";
import Layers from "slotworks/graphics/layers/layers";
import * as filters from 'pixi-filters';
import settingsModel from "slotworks/model/settings/settings-model";
import g288SettingsModel from "model/g288-settings-model";

export default class G288SpinReelTransition extends AbstractReelTransition {

    public onReelLand: Signal = new Signal();

    private spinners: G288ReelSpinner[];

    private startGap: number = 100;
    private stopGap: number = 100;
    private quickStopGap: number = 50;

    private allowPartialSkips: boolean = true;

    private anticipation: AnticipationInfo;
    private anticipatingSymbols: SymbolComponent[];

    private stackAddTriggered: boolean;

    private spinningReels: number;
    private skipping: boolean;
    private skippingFirst: boolean;

    private heldReels: number[] = [];

    // Race handler function, see checkRaceComplete
    private completeResolve: Function;

    constructor(startGap: number, stopGap: number, quickStopGap: number, allowPartialSkips: boolean) {
        super();

        this.startGap = startGap;
        this.stopGap = stopGap;
        this.quickStopGap = quickStopGap;
        this.allowPartialSkips = allowPartialSkips;
    }

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]) {
        super.init(reels, stops, reelset);

        this.spinners = [];

        const config = Components.config.matrix;
        if (config.transition.options.spinStages) {
            G288ReelSpinner.spinStages = config.transition.options.spinStages;
        }
        if (config.transition.options.skipStages) {
            G288ReelSpinner.skipStages = config.transition.options.skipStages;
        }
        if (config.transition.options.anticipationStages) {
            G288ReelSpinner.anticipationStages = config.transition.options.anticipationStages;
        }
        G288ReelSpinner.stackStages = config.transition.options.stackStages;
        G288ReelSpinner.minRepeats = 20;

        for (let i = 0; i < reels.length; i++) {
            const reel = reels[i];
            const spinner = new G288ReelSpinner(reel, stops[i], reelset[i]);
            this.spinners.push(spinner);
            spinner.landSignal.add((index: number) => this.reelLandedHandler(index));
            if(i == 0) {
                spinner.landSignal.add(() => this.onReel1AnticipateEnd());
            }
        }

        G288ReelSpinner.reel1AnticipationStart.add(() => this.onReel1AnticipateStart());
    }

    public start(reelset?: string[][], jumpStart: boolean = false): Contract<void> {
        this.skipping = false;
        this.skippingFirst = false;

        SoundManager.event(SoundEvent.reel_transition_start);

        Components.matrix.resetSymbols();

        const startMethods = [];

        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
            if (this.heldReels.indexOf(spinnerIndex) === -1) {
                const spinner = this.spinners[spinnerIndex];
                startMethods.push(() => spinner.start(jumpStart ? 0 : this.startGap * spinnerIndex, jumpStart));
            }
        }

        this.spinningReels = startMethods.length;

        return parallel(startMethods);
    }

    public stop(stops: number[], reelset: string[][], quickSpin: boolean, anticipation: AnticipationInfo): Contract<void> {
        this.anticipatingSymbols = [];
        this.anticipation = anticipation;
        const anticipations = anticipation.reelAnticipations;

        let stopMethods: Array<() => Contract<void>> = [];

        let anticipating = false;

        const needQuickerSpin = g288SettingsModel.needQuickerSpin();

        if (anticipations) {
            for (const anticipate of anticipations) {
                if (anticipate) {
                    anticipating = true;
                }
            }
        }

        // stack add, reels and stops from grid-only-supplement
        const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;
        this.stackAddTriggered = record.stackAddFeatureTriggered;

        if(this.stackAddTriggered) {
            anticipating = false;
        //    this.spinners[0].stackadd();
            stopMethods.push(() => this.spinners[0].stop(stops[0], reelset[0], 0, quickSpin));
        //    stopMethods.push(() => this.highlight(this.reels[0].getSymbols()));
            stopMethods.push(() => Contract.wrap(() => {
                for (let spinnerIndex = 1; spinnerIndex < this.spinners.length; spinnerIndex++) {
                    const spinner = this.spinners[spinnerIndex];
                    spinner.stackadd();
                    spinner.stackAddFade();
                }
            }));
            stopMethods.push(() => G288Components.stackAdd.start(this.spinners));
            const restMethods: Array<() => Contract<void>> = [];
            
            for (let spinnerIndex = 1; spinnerIndex < this.spinners.length; spinnerIndex++) {
                const spinner = this.spinners[spinnerIndex];
                
                restMethods.push(
                    () => sequence([
                        () => spinner.stop(stops[spinnerIndex], reelset[spinnerIndex], spinnerIndex * 800, false, true),
                        () => G288Components.stackAdd.afterReelStop(spinnerIndex)
                    ]));
            }
            stopMethods.push(() => parallel(restMethods));

            return new Contract<void>((resolve) => {
                sequence(stopMethods).then(() => {     
                    G288Components.stackAdd.end();               
                    SoundManager.event(SoundEvent.reel_transition_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {

            const offset = quickSpin ? 0 : needQuickerSpin ? 1100 : 2000;
            let index = 0;
            for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
                if (this.heldReels.indexOf(spinnerIndex) !== -1) {
                    continue;
                }

                const spinner = this.spinners[spinnerIndex];

                if (anticipations && anticipations[spinnerIndex]) {
                    stopMethods.push(() => spinner.anticipate(stops[spinnerIndex], reelset[spinnerIndex]));
                    index = 0;
                } else {
                    const gap = (quickSpin ? (settingsModel.isQuickSpinDisabled() ? 180 : this.quickStopGap) : this.stopGap) * index;
                    stopMethods.push(() => spinner.stop(stops[spinnerIndex], reelset[spinnerIndex], gap + (gap > 0 ? offset : 0), quickSpin));
                    index++;
                }
            }
        }

        if (!anticipating) {
            // If not anticipating, all reels are stopped at the same time (the delay between reels is internal)
            return new Contract<void>((resolve) => {
                parallel(stopMethods).then(() => {
                    SoundManager.event(SoundEvent.reel_transition_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            // If anticipation is present, subsequent reels are delayed until the anticipation is finished (since the gap is unpredictable),
            // and then are stopped in parallel until the next anticipation reel and so on
            return new Contract<void>((resolve) => {
                let anticipationStarted = false;

                const getNextStop = (stop: number, parallels: Array<() => Contract<void>>): Contract<any> => {
                    if (stopMethods.length > stop) {
                        if (!anticipations[stop]) {
                            parallels.push(stopMethods[stop]);
                            return getNextStop(stop + 1, parallels);
                        } else {
                            return parallel(parallels).then(() => {
                                if (!anticipationStarted) {
                                    SoundManager.event(SoundEvent.reel_transition_anticipate);
                                    anticipationStarted = true;
                                }
                                stopMethods[stop]().then(() => getNextStop(stop + 1, []));
                            });
                        }
                    } else {
                        if (parallels.length) {
                            parallel(parallels).then(() => {
                                SoundManager.event(SoundEvent.reel_transition_end);
                                this.checkRaceComplete(() => resolve(null));
                            });
                        } else {
                            SoundManager.event(SoundEvent.reel_transition_end);
                            this.checkRaceComplete(() => resolve(null));
                        }
                    }
                };
                getNextStop(0, []);
            });
        }
    }

    public skipFirst(stops: number[], reelset: string[][]) {
        if(!this.spinners[0].isAnimating())
            return false;
        if(!this.skippingFirst) {
            this.skippingFirst = true;
            this.spinners[0].setSkipping(stops[0], reelset[0]);
            setTimeout(() => G288Components.stackArrows.skip(), 400);            
        }
        return true;
    }

    public skip(stops: number[], reelset: string[][]): Contract<void> {
        this.skipping = true;
        let alreadyStopping = false;
        for (const spinner of this.spinners) {
            if (spinner.isStopping()) {
                alreadyStopping = true;
                break;
            }
        }

        if (!alreadyStopping || this.allowPartialSkips) {
            const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;

            SoundManager.event(SoundEvent.reel_transition_skip);
            const skipMethods: Array<() => Contract<void>> = [];
            
            if(record.stackAddFeatureTriggered)
                G288Components.stackAdd.skip();

            for (let i = 0; i < this.spinners.length; i++) {
                skipMethods.push(() => this.spinners[i].skip(stops[i], reelset[i]));
            }
            return new Contract<void>((resolve) => {
                parallel(skipMethods).then(() => {    
                    if(record.stackAddFeatureTriggered) {
                        G288Components.stackAdd.skipEnd();
                    }                                 
                    SoundManager.event(SoundEvent.reel_transition_skip_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
            
        } else {
            return new Contract<void>((resolve) => null);
        }
    }

    public jump(stops: number[], reelset: string[][]) {
        for (let i = 0; i < this.spinners.length; i++) {

            const spinner = this.spinners[i];

            spinner.jump(stops[i], reelset[i]);
        }
    }

    public reelLandedHandler(reelIndex: number) {
        this.onReelLand.dispatch(reelIndex);
        const lands = [];
        const landedReel = Components.matrix.getReels()[reelIndex - 1];
        const reelSymbols = landedReel.getSymbols();
        const validLandSymbols = this.anticipation.landSymbols[reelIndex - 1];
        const hasAnticipation = (this.anticipation && this.anticipation.anticipateSymbols.length > reelIndex);
        const symbolsToAnimate = hasAnticipation ? this.anticipation.anticipateSymbols[reelIndex] : [];
        for (const symbol of reelSymbols) {
            if (!validLandSymbols || validLandSymbols.indexOf(symbol.symbolId) > -1) {
                if(symbol.symbolId == "SCATTER") {
                    symbol.setLayer(Layers.SymbolAnimations);
                    symbol.lockLayer();
                    lands.push(() => symbol.land());
                }
            } else {
                if(symbol.symbolId == "SCATTER") {
                    symbol.setLayer(Layers.SymbolAnimations);
                    symbol.lockLayer();
                }
            }
        }
        
        parallel(lands).then(() => {
            this.spinningReels--;
            this.checkRaceComplete();

            if (!this.skipping) {
                if (symbolsToAnimate.length) {
                    let symbols: SymbolComponent[] = [];
                    const reels = Components.matrix.getReels().slice(0, reelIndex);
                    for (const reel of reels) {
                        if (reel.index <= reelIndex) {
                            symbols = symbols.concat(reel.getSymbols());
                        }
                    }
                    for (const symbol of symbols) {
                        if (symbolsToAnimate.indexOf(symbol.symbolId) > -1) {
                            if (this.anticipatingSymbols.indexOf(symbol) === -1) {
                                symbol.anticipate().execute();
                                this.anticipatingSymbols.push(symbol);
                            }
                        }
                    }
                }
            }
        });
    }

    public hold(reelIndexes?: number[]) {
        if (!reelIndexes) {
            reelIndexes = [];
        }
        this.heldReels = reelIndexes;
    }

    public update() {
        super.update();

        for (const spinner of this.spinners) {
            spinner.update();
        }
    }

    public getSpinners() {
        return this.spinners;
    }

    public setReelset(reelset: string[][]) {
        for(let i = 0; i < this.spinners.length; i++) {
            this.spinners[i].setReelstrip(reelset[i]);
        }
    }

    public resetReelset() {
        for(let spinner of this.spinners) {
            spinner.resetReelstrip();
        }
    } 

    private stopAnticipatingSymbols() {
        for (const symbol of this.anticipatingSymbols) {
            symbol.static();
        }
        this.anticipatingSymbols = [];
    }

    private onReel1AnticipateStart() {
        for(let i = 1; i < this.spinners.length; i++) {
            this.spinners[i].fade(true);
        }
    }

    private onReel1AnticipateEnd() {
        const record = gameState.getCurrentGame().getCurrentRecord() as G288SpinRecord;
        if(!record.stackAddFeatureTriggered) {
            for(let i = 1; i < this.spinners.length; i++) {
                this.spinners[i].fade(false);
            }
        }
    }

    // Resolves race condition between symbol land animation and reels stopping
    private checkRaceComplete(resolve?: Function) {
        if (resolve) {
            this.completeResolve = resolve;
        }
        if (this.spinningReels === 0) {
            if (this.completeResolve) {
                this.stopAnticipatingSymbols();
                this.completeResolve();
                this.completeResolve = null;
                G288Components.stackArrows.hideRightArrows();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/matrix/transitions/spin-reel-transition.ts